import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AccountingAccountEndpointEnum,
  ApiPathEnum,
  BankEndpointEnum,
  CountryEndPointEnum,
  FinancialEndPointEnum,
} from '../constants/endpoints.enum';
import { PeriodType } from '../constants/constants';
import {
  FinancialMovementsResponse,
  PendingMovementIdsResponse,
} from '../interfaces/api/financial-api.interface';
import {
  DashboardSummary,
  MoneyFlowResponse,
} from '../interfaces/ui/financial-dashboard.interface';
import {
  StatusChangePayload,
  StatusChangeResult,
  DecisionHistoryItem,
} from '../interfaces/ui/status-change-modal.interface';
import {
  ApiResponse,
  PaginatedResponse,
} from '../interfaces/api/common.interface';
import {
  AccountingAccountDetailOption,
  AccountingAccountOption,
  AccountingCategoryOption,
  AccountingCompanyOption,
  BankOption,
  CreateMovementResult,
} from '../interfaces/ui/create-movement-modal.interface';
import {
  BatchStatusChangePayload,
  BatchStatusChangeResult,
} from '../interfaces/ui/batch-status-modal.interface';
import {
  ForkMovementResult,
  ForkCountResult,
} from '../interfaces/api/fork-movement.interface';
import { FilterOptions } from '../interfaces/api/filter-options.interface';
import {
  MovementFilters,
  filtersToQueryParams,
} from '../interfaces/ui/movement-filters.interface';
import { ExportDataResponse } from '../interfaces/api/export-data-response.interface';
import { BankSelectResponse } from '../interfaces/bank.interface';
import { AccountingAccountSelectOption } from '../interfaces/accounting-account.interface';

@Injectable({
  providedIn: 'root',
})
export class FinancialService {
  private readonly API_BANK = `${environment.API_URL}${ApiPathEnum.Bank}`;
  private readonly API_URL_ACCOUNTING_ACCOUNT = `${environment.API_URL}${ApiPathEnum.AccountingAccount}`;
  private readonly API_URL_COUNTRY = `${environment.API_URL}${ApiPathEnum.Country}`;
  private readonly API_URL_FINANCIAL = `${environment.API_URL}${ApiPathEnum.Financial}`;
  private readonly API_ACCOUNTING_CATALOG = `${environment.API_URL}/accounting-catalog`;

  constructor(private http: HttpClient) {}

  getDashboardSummary(
    dateStart?: string,
    dateEnd?: string
  ): Observable<ApiResponse<DashboardSummary>> {
    let params = new HttpParams();

    if (dateStart) {
      params = params.set('date_start', dateStart);
    }
    if (dateEnd) {
      params = params.set('date_end', dateEnd);
    }
    return this.http.get<ApiResponse<DashboardSummary>>(
      `${this.API_URL_FINANCIAL}${FinancialEndPointEnum.DashboardSummary}`,
      { params }
    );
  }

  getMoneyFlow(
    period: PeriodType = 'weekly',
    dateStart?: string,
    dateEnd?: string
  ): Observable<ApiResponse<MoneyFlowResponse>> {
    let params = new HttpParams().set('period', period);

    if (dateStart) {
      params = params.set('date_start', dateStart);
    }
    if (dateEnd) {
      params = params.set('date_end', dateEnd);
    }
    
    return this.http.get<ApiResponse<MoneyFlowResponse>>(
      `${this.API_URL_FINANCIAL}${FinancialEndPointEnum.DashboardMoneyFlow}`,
      { params }
    );
  }
  getFilterOptions(): Observable<ApiResponse<FilterOptions>> {
    return this.http.get<ApiResponse<FilterOptions>>(
      `${this.API_URL_FINANCIAL}/filter-options`
    );
  }

  getFinancialMovements(
    page: number,
    limit: number,
    filters?: MovementFilters | null,
    sortDirection?: 'asc' | 'desc' | null
  ): Observable<ApiResponse<FinancialMovementsResponse>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (sortDirection) {
      params = params.set('sortDirection', sortDirection);
    }

    if (filters) {
      const queryParams = filtersToQueryParams(filters);
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, value);
        }
      });
    }

    return this.http.get<ApiResponse<FinancialMovementsResponse>>(
      `${this.API_URL_FINANCIAL}${FinancialEndPointEnum.GetFinancial}`,
      { params }
    );
  }

  /** Ids of every Pending movement matching the filters (no pagination). */
  getPendingMovementIds(
    filters?: MovementFilters | null
  ): Observable<ApiResponse<PendingMovementIdsResponse>> {
    let params = new HttpParams();

    if (filters) {
      const queryParams = filtersToQueryParams(filters);
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, value);
        }
      });
    }

    return this.http.get<ApiResponse<PendingMovementIdsResponse>>(
      `${this.API_URL_FINANCIAL}${FinancialEndPointEnum.GetFinancial}/pending-ids`,
      { params }
    );
  }

  getFinancialMovement(id: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${this.API_URL_FINANCIAL}${FinancialEndPointEnum.GetFinancialOne}/${id}`
    );
  }

  getFinancialMovementDecisions(
    financialId: string
  ): Observable<ApiResponse<DecisionHistoryItem[]>> {
    return this.http.get<ApiResponse<DecisionHistoryItem[]>>(
      `${this.API_URL_FINANCIAL}${FinancialEndPointEnum.GetFinancialOne}/${financialId}/decisions`
    );
  }

  updateFinancialStatus(
    payload: StatusChangePayload
  ): Observable<ApiResponse<StatusChangeResult>> {
    const endpoint = FinancialEndPointEnum.UpdateFinancialStatus.replace(
      ':id',
      payload.financialId
    );
    return this.http.patch<ApiResponse<StatusChangeResult>>(
      `${this.API_URL_FINANCIAL}${endpoint}`,
      payload
    );
  }

  deleteFinancialMovement(
    financialId: string
  ): Observable<ApiResponse<unknown>> {
    return this.http.delete<ApiResponse<unknown>>(
      `${this.API_URL_FINANCIAL}${FinancialEndPointEnum.DeleteFinancial}/${financialId}`
    );
  }

  getAllBankData(): Observable<ApiResponse<PaginatedResponse<BankOption>>> {
    return this.http.get<ApiResponse<PaginatedResponse<BankOption>>>(
      `${this.API_BANK}/get-all`
    );
  }

  getBanksSelect(
    page: number = 1,
    limit: number = 100,
    searchTerm: string = '',
    countryId?: number
  ): Observable<BankSelectResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }

    return this.http.get<BankSelectResponse>(
      `${this.API_BANK}/get-banks-select`,
      {
        params,
      }
    );
  }

  getAllAccountingAccountData(): Observable<any> {
    return this.http.get<any>(
      `${this.API_URL_ACCOUNTING_ACCOUNT}${AccountingAccountEndpointEnum.GetAccountingAccountData}`
    );
  }

  getAllCountries(): Observable<ApiResponse<unknown>> {
    return this.http.get<ApiResponse<unknown>>(
      `${this.API_URL_COUNTRY}${CountryEndPointEnum.GetCountryData}`
    );
  }

  getLastInvoiceNumber(): Observable<ApiResponse<{ numero_factura: number }>> {
    return this.http.get<ApiResponse<{ numero_factura: number }>>(
      `${this.API_URL_FINANCIAL}/last-invoice`
    );
  }

  getLastSpentNumber(): Observable<ApiResponse<{ numero_gasto: number }>> {
    return this.http.get<ApiResponse<{ numero_gasto: number }>>(
      `${this.API_URL_FINANCIAL}/last-spent`
    );
  }

  getLastFlashMovement(): Observable<
    ApiResponse<{ numero_movimiento_rapido: number }>
  > {
    return this.http.get<ApiResponse<{ numero_movimiento_rapido: number }>>(
      `${this.API_URL_FINANCIAL}${FinancialEndPointEnum.GetLastFlashMovement}`
    );
  }

  createFinancial(
    formData: FormData
  ): Observable<ApiResponse<CreateMovementResult>> {
    return this.http.post<ApiResponse<CreateMovementResult>>(
      `${this.API_URL_FINANCIAL}/create`,
      formData
    );
  }

  updateFinancial(
    id: number,
    formData: FormData
  ): Observable<ApiResponse<CreateMovementResult>> {
    return this.http.patch<ApiResponse<CreateMovementResult>>(
      `${this.API_URL_FINANCIAL}/movement/${id}`,
      formData
    );
  }

  getAccountingCompanies(): Observable<ApiResponse<AccountingCompanyOption[]>> {
    return this.http.get<ApiResponse<AccountingCompanyOption[]>>(
      `${this.API_ACCOUNTING_CATALOG}/companies/select`
    );
  }

  getAccountingCategories(
    companyId: number
  ): Observable<ApiResponse<AccountingCategoryOption[]>> {
    return this.http.get<ApiResponse<AccountingCategoryOption[]>>(
      `${this.API_ACCOUNTING_CATALOG}/categories/select/${companyId}`
    );
  }

  getAccountingAccounts(
    categoryId: number
  ): Observable<ApiResponse<AccountingAccountDetailOption[]>> {
    return this.http.get<ApiResponse<AccountingAccountDetailOption[]>>(
      `${this.API_ACCOUNTING_CATALOG}/accounts/select/${categoryId}`
    );
  }

  uploadDocuments(financialId: number, formData: FormData): Observable<any> {
    return this.http.post(
      `${this.API_URL_FINANCIAL}/movement/${financialId}/upload`,
      formData
    );
  }

  updateFinancialStatusBatch(
    payload: BatchStatusChangePayload
  ): Observable<ApiResponse<BatchStatusChangeResult>> {
    return this.http.patch<ApiResponse<BatchStatusChangeResult>>(
      `${this.API_URL_FINANCIAL}/movements/batch-status`,
      payload
    );
  }

  forkFinancialMovement(
    financialId: string,
    userId: number
  ): Observable<ApiResponse<ForkMovementResult>> {
    return this.http.post<ApiResponse<ForkMovementResult>>(
      `${this.API_URL_FINANCIAL}/movement/${financialId}/fork`,
      { userId }
    );
  }

  getForkCount(financialId: string): Observable<ApiResponse<ForkCountResult>> {
    return this.http.get<ApiResponse<ForkCountResult>>(
      `${this.API_URL_FINANCIAL}/movement/${financialId}/fork-count`
    );
  }

  exportFinancialMovements(
    filters?: MovementFilters | null,
    limit?: number | null
  ): Observable<ApiResponse<ExportDataResponse>> {
    let params = new HttpParams();

    if (filters) {
      const queryParams = filtersToQueryParams(filters);
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, value);
        }
      });
    }

    if (limit) {
      params = params.set('limit', limit.toString());
    }

    return this.http.get<ApiResponse<ExportDataResponse>>(
      `${this.API_URL_FINANCIAL}/export`,
      { params }
    );
  }

  getAccountingAccountsForSelect(): Observable<
    ApiResponse<AccountingAccountSelectOption[]>
  > {
    return this.http.get<ApiResponse<AccountingAccountSelectOption[]>>(
      `${this.API_ACCOUNTING_CATALOG}/accounts/select-all`
    );
  }
}
